import { tapi as tradingApi } from 'src/api';
import type {
    BasketSpec,
    NewBasketSpec,
    BasketSpecPatch,
} from 'src/routes/widgets/BasketTradingWidget/state/basket-spec';

type ApiBasketType = 'quantity' | 'weighted' | 'notional' | 'position-adjustment';

function toApiBasketType(type: BasketSpec['basketType']): ApiBasketType {
    return type === 'positionAdjustment' ? 'position-adjustment' : type;
}

function fromApiBasketType(type: ApiBasketType): BasketSpec['basketType'] {
    return type === 'position-adjustment' ? 'positionAdjustment' : type;
}

function mapSpecFromApi(data: Record<string, unknown>): BasketSpec {
    return { ...data, basketType: fromApiBasketType(data.basketType as ApiBasketType) } as BasketSpec;
}

export async function getBasketSpecs(params?: Record<string, string>): Promise<BasketSpec[]> {
    const response = await tradingApi.get<Record<string, unknown>[]>('/baskets/specs', {
        params,
        validateStatus: (status) => (status >= 200 && status < 300) || status === 404,
    });

    if (response.status === 404) {
        return [];
    }

    return Array.isArray(response.data) ? response.data.map(mapSpecFromApi) : [];
}

export async function getBasketSpecById(id: string): Promise<BasketSpec | null> {
    const response = await tradingApi.get<Record<string, unknown>>(`/baskets/specs/${encodeURIComponent(id)}`, {
        validateStatus: (status) => (status >= 200 && status < 300) || status === 404,
    });

    if (response.status === 404) {
        return null;
    }

    return mapSpecFromApi(response.data);
}

export async function createBasketSpec(spec: NewBasketSpec): Promise<BasketSpec> {
    const payload = { ...spec, basketType: toApiBasketType(spec.basketType) };
    const response = await tradingApi.post<Record<string, unknown>>('/baskets/specs', payload);
    return mapSpecFromApi(response.data);
}

export async function updateBasketSpec(id: string, patch: BasketSpecPatch): Promise<BasketSpec> {
    const payload = patch.basketType != null ? { ...patch, basketType: toApiBasketType(patch.basketType) } : patch;
    const response = await tradingApi.patch<Record<string, unknown>>(
        `/baskets/specs/${encodeURIComponent(id)}`,
        payload,
    );
    return mapSpecFromApi(response.data);
}

export async function deleteBasketSpec(id: string): Promise<void> {
    await tradingApi.delete(`/baskets/specs/${encodeURIComponent(id)}`);
}
